
import { EAxiosResponseCode, getHttpErrorMessage, Http } from '@core/api';
import { getHostbaseUrl } from '@core/tools';
import { EYibaoSignType, IMasterBank, IQualificationParam, ISceneQualificationParam, IYibaoBaseInfo, IYibaoCnapsCode, IYibaoDetail, IYibaoShop, IYopAccount, IYopAccountsParam, IYopCompanyList } from '../../types/yibao';
import { getYibaoOpeningSaveParam } from './yibao-utils';
import { IYibaoApplySubmitParam, IYibaoLicenseSaveParam } from '../../src/components';
import { ECompanyType } from '../../types/common';

const hostUrl = getHostbaseUrl();

/**
 * 获取易宝钱包基础信息
 */
export async function queryYibaoWallet(companyTerminal = false) {
  let data = {} as IYibaoBaseInfo;

  try {
    const { code, context, message } = await Http.get<IYibaoBaseInfo>(
      `${hostUrl}/pay/yop/${companyTerminal ? 'company' : 'agent'}/get-merchant-base-info`,
      {},
    );

    if (code === EAxiosResponseCode.Succeed) {
      data = context || {} as IYibaoBaseInfo;

      data.openingProducts = data.openingProducts || [];
      data.companyBalance = data.companyBalance || {};
      data.agentBalance = data.agentBalance || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 获取易宝账户详细信息
 */
export async function queryYibaoDetail(id: number, companyTerminal = false) {
  let data = {} as IYibaoDetail;

  try {
    const { code, context, message } = await Http.get<IYibaoDetail>(
      `${hostUrl}/pay/yop/${companyTerminal ? 'company' : 'agent'}/get-merchant-detail`,
      { id },
    );

    if (code === EAxiosResponseCode.Succeed) {
      data = context || {} as IYibaoDetail;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 申请易宝账户 - 上传营业执照信息保存
 * @param agentType 0:企业代理商,1:个人代理商
 */
export async function submitYibaoLicense(param: IYibaoLicenseSaveParam, agentType: 0 | 1) {
  let info = {} as IYibaoDetail;

  param = JSON.parse(JSON.stringify(param)) as IYibaoLicenseSaveParam;
  param.signType = agentType === 1 ? EYibaoSignType.INDIVIDUAL : EYibaoSignType.ENTERPRISE;

  try {
    const { code, context, message } = await Http.post<IYibaoDetail>(
      `${hostUrl}/pay/yop/agent/merchant-subject`,
      param,
    );

    if (code === EAxiosResponseCode.Succeed) {
      info = context || {} as IYibaoDetail;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return info;
}

/**
 * 申请易宝账户 - 企业端 -上传营业执照信息保存
 * @param companyType {@link ECompanyType}
 */
export async function submitYibaoCompanyLicense(param: IYibaoLicenseSaveParam, companyType: ECompanyType) {
  let info = {} as IYibaoDetail;

  param = JSON.parse(JSON.stringify(param)) as IYibaoLicenseSaveParam;
  param.signType = companyType === ECompanyType.Personal ? EYibaoSignType.INDIVIDUAL : EYibaoSignType.ENTERPRISE;

  try {
    const { code, context, message } = await Http.post<IYibaoDetail>(
      `${hostUrl}/pay/yop/company/merchant-subject`,
      param,
    );

    if (code === EAxiosResponseCode.Succeed) {
      info = context || {} as IYibaoDetail;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return info;
}

/**
 * 申请易宝账户 - 法人信息保存
 */
export async function submitYibaoLegalPerson<T extends Record<string, any>>(param: T, companyTerminal = false) {
  let info = {} as IYibaoDetail;

  param = JSON.parse(JSON.stringify(param)) as T;

  try {
    const { code, context, message } = await Http.post<IYibaoDetail>(
      `${hostUrl}/pay/yop/${companyTerminal ? 'company' : 'agent'}/merchant-corporation`,
      { ...param },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    } else {
      info = context || {} as IYibaoDetail;
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return info;
}

/**
 * 申请电子账户 - 受益人信息保存
 */
export async function submitYibaoBeneficiary(param: any) {
  param = JSON.parse(JSON.stringify(param)) as any;

  try {
    const { code, message } = await Http.post<IYibaoDetail>(
      `${hostUrl}/pay/yop/company/ubo`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 申请易宝账户 - 开户绑卡信息保存
 */
export async function submitYibaoOpenParam(param: any, companyTerminal = false) {
  param = JSON.parse(JSON.stringify(param)) as any;
  const p = getYibaoOpeningSaveParam(param as any);

  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/yop/${companyTerminal ? 'company' : 'agent'}/bank-account`,
      p,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 申请易宝账户 - 提交
 */
export async function submitApplyYibao(param: IYibaoApplySubmitParam, companyTerminal = false) {
  param = JSON.parse(JSON.stringify(param)) as IYibaoApplySubmitParam;

  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/yop/${companyTerminal ? 'company' : 'agent'}/submit-register`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 申请电子账户 - 保存平台店铺信息
 */
export async function saveElectronicShop(param: { id: number; shopList: IYibaoShop[] }) {
  param = JSON.parse(JSON.stringify(param)) as { id: number; shopList: IYibaoShop[] };

  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/yop/company/merchant-shop`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 开通公域收款产品 - 提交
 */
export async function submitApplyElectronic(param: IYibaoApplySubmitParam) {
  param = JSON.parse(JSON.stringify(param)) as IYibaoApplySubmitParam;

  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/yop/company/public-collection-open`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 开通线下收单产品 - 提交
 */
export async function submitOfflineReceipt(id: number) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/yop/company/offline-receipt-open`,
      { id },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 申请电子账户 - 资质文件提交
 */
export async function submitSceneQualification(param: ISceneQualificationParam) {
  param = JSON.parse(JSON.stringify(param)) as ISceneQualificationParam;

  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/yop/company/product-scene-qualification`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 申请电子账户 (代理商) - 资质文件提交
 */
export async function submitQualification(param: IQualificationParam) {
  param = JSON.parse(JSON.stringify(param)) as IQualificationParam;

  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/yop/agent/product-scene-qualification`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 查询支行&&大额行号列表
 */
export async function queryYibaoCnapsCodes(param?: {
  cnapsName?: string;
  cnapsCode?: string;
  provinceCode: string;
  cityCode: string;
  bankCode: string;
}) {
  let items: Array<IYibaoCnapsCode> = [];

  try {
    const { code, context, message } = await Http.post<IYibaoCnapsCode[]>(`${hostUrl}/pay/yop/base/bankcode`, param);

    if (code === EAxiosResponseCode.Succeed) {
      items = context || [];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return items;
}

/**
 * 查询易宝总行列表
 */
export async function queryMasterBanks(bankName = '', bankCode = '') {
  let banks = [] as IMasterBank[];

  try {
    const { code, context, message } = await Http.post<IMasterBank[]>(
      `${hostUrl}/pay/yop/base/master-bank-list`,
      {
        bankName,
        bankCode,
      },
    );

    if (code === EAxiosResponseCode.Succeed) {
      banks = context || [];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return banks;
}

/**
 * 空中分账 - 易宝账户 - 提现
 */
export async function YibaoWithdraw(params, companyTerminal = false) {

  let data = {};

  try {
    const { code, context, message } = await Http.post(`${hostUrl}/pay/yop/merchant-withdraw/${companyTerminal ? 'company' : 'agent'}`, params);

    if (code === EAxiosResponseCode.Succeed) {
      const res = context;

      data = res || {};
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
  return data;
}

/**
 * 分页查询企业账户列表
 */
export async function queryYopAccountList(params: IYopAccountsParam, term: 'agent' | 'company') {
  let total = 0;
  let data = [] as IYopAccount[];

  try {
    const { code, context, message } = await Http.post<{ data: IYopAccount[]; total: number }>(
      `${hostUrl}/pay/yop/account/query/${term === 'agent' ? 'agent' : 'company'}-list`,
      { ...params },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: IYopAccount[]; total: number };

      data = res.data || [];
      total = res.total || 0;

      data.forEach((item) => {
        item.productFees = item.productFees || [];
      });
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 易宝商户状态检查
 */
export async function merchantStatusCheck() {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/yop/company/merchant-status-check`,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 查询易宝已入网且操作授权已确认的公司列表
 */
export async function queryYopOperateAuthorizedCompanies() {
  let data = [] as IYopCompanyList[];

  try {
    const { code, context, message } = await Http.post<IYopCompanyList[]>(
      `${hostUrl}/pay/yop/account/query/operate-auth-authorized-companies`,
      null,
    );

    if (code === EAxiosResponseCode.Succeed) {
      data = context || [] as IYopCompanyList[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}